import React from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Box,
  Badge,
  Tooltip,
} from '@mui/material';
import { AlertTriangle, Clock } from 'lucide-react';
import { Case } from '../../../types';
import { useStatusColor } from '../../../hooks/useStatusColor';
import { TaskCounts } from '../../../types';
import { getTimeInStage } from '../../../utils/dates';

interface CasesTableProps {
  cases: Case[];
  getTaskCounts: (caseId: string) => TaskCounts;
  onCaseClick: (caseId: string) => void;
}

export const CasesTable: React.FC<CasesTableProps> = ({
  cases,
  getTaskCounts,
  onCaseClick,
}) => {
  const getStatusColor = useStatusColor();

  // Calculate active stage task counts
  const getActiveStageTaskCounts = (case_: Case): TaskCounts => {
    const taskCounts = getTaskCounts(case_.id);
    const activeStageTasks = case_.tasks.filter(task => 
      task.stageId === case_.currentStageId
    );
    
    const pendingCount = activeStageTasks.filter(task => 
      task.status !== 'completed' && new Date(task.dueDate) > new Date()
    ).length;
    
    const urgentCount = activeStageTasks.filter(task => 
      task.status !== 'completed' && new Date(task.dueDate) < new Date()
    ).length;

    return {
      total: activeStageTasks.length,
      pending: pendingCount,
      urgent: urgentCount,
    };
  };

  const getTimeChipColor = (timeInStage: string): 'error' | 'info' => {
    const match = timeInStage.match(/(\d+)\s*(days?|hours?|minutes?|months?)/);
    if (!match) return 'info';

    const [, value, unit] = match;
    const numValue = parseInt(value, 10);

    if (unit.startsWith('month')) return 'error';
    if (unit.startsWith('day') && numValue >= 10) return 'error';
    return 'info';
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Case Number</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Practice Area</TableCell>
            <TableCell>Time in Stage</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Current Stage Tasks</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cases.map((case_) => {
            const taskCounts = getActiveStageTaskCounts(case_);
            const timeInStage = getTimeInStage(case_.timeline, case_.currentStageId);
            const chipColor = getTimeChipColor(timeInStage);
            
            return (
              <TableRow 
                key={case_.id} 
                hover
                onClick={() => onCaseClick(case_.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{case_.caseNumber}</TableCell>
                <TableCell>{case_.title}</TableCell>
                <TableCell>{case_.practiceArea}</TableCell>
                <TableCell>
                  <Chip
                    icon={<Clock size={14} />}
                    label={timeInStage}
                    size="small"
                    color={chipColor}
                    variant="outlined"
                    sx={{
                      '& .MuiChip-icon': {
                        ml: 1,
                        mr: 0.5,
                      },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={case_.status}
                    color={getStatusColor(case_.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    {taskCounts.urgent > 0 && (
                      <Tooltip title="Overdue tasks">
                        <Badge badgeContent={taskCounts.urgent} color="error">
                          <AlertTriangle size={18} />
                        </Badge>
                      </Tooltip>
                    )}
                    {taskCounts.pending > 0 && (
                      <Tooltip title="Pending tasks">
                        <Badge badgeContent={taskCounts.pending} color="warning">
                          <Clock size={18} />
                        </Badge>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};